import { Component, input, signal, computed, HostListener, output, ElementRef, viewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SafeUrlPipe } from 'app/core/pipes/safe-url.pipe';
import { AppSettings } from '@services/settings/app-settings.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';

export interface DocViewerItem {
  id: string | number;
  uuid?: string | number;
  imageUrl?: string;
  title?: string;
  status?: string;
  /** MIME del archivo (ej. application/pdf, image/webp). */
  mimeType?: string | null;
  fileName?: string | null;
  /** Fuerza el tipo de vista si se conoce de antemano. */
  kind?: 'image' | 'pdf';
}

/** Detecta PDF por kind, mime, nombre o URL. */
export function isPdfViewerItem(item: DocViewerItem | null | undefined, fallbackUrl?: string | null): boolean {
  if (!item && !fallbackUrl) return false;
  if (item?.kind === 'pdf') return true;
  if (item?.kind === 'image') return false;
  const mime = String(item?.mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return true;
  if (mime.startsWith('image/')) return false;
  const name = String(item?.fileName || '').toLowerCase();
  if (name.endsWith('.pdf')) return true;
  if (/\.(jpe?g|png|gif|webp|bmp)$/i.test(name)) return false;
  const url = String(item?.imageUrl || fallbackUrl || '').toLowerCase();
  return /\.pdf(\?|#|$)/i.test(url);
}

@Component({
  selector: 'app-viewer',
  standalone: true,
  imports: [CommonModule, SafeUrlPipe, MobileFooterComponent],
  templateUrl: './viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./viewer.component.scss']
})
export class ViewerComponent {
  readonly appSettings = inject(AppSettings);

  items = input<DocViewerItem[]>([]);
  activeId = input<string | number | null>(null);
  title = input<string>('Visor de Documentos');
  allowDelete = input<boolean>(false);
  imageUrl = input<string | undefined | null>();

  activeItem = computed(() => {
    const id = this.activeId();
    if (id === null || id === undefined) return undefined;
    return this.items().find(i => i.id === id);
  });
  activeIndex = computed(() => {
    const id = this.activeId();
    if (id === null || id === undefined) return -1;
    return this.items().findIndex(i => i.id === id);
  });

  activeSrc = computed(() => this.activeItem()?.imageUrl ?? this.imageUrl() ?? null);

  isPdf = computed(() => isPdfViewerItem(this.activeItem(), this.imageUrl()));

  close = output<void>();
  onSelect = output<string | number>();
  onDelete = output<string | number>();
  // Emite imagen rotada en base64
  onImageChange = output<string>();

  zoomLevel = signal(1);
  translateX = signal(0);
  translateY = signal(0);
  isDragging = signal(false);
  rotation = signal(0);

  imageElement = viewChild<ElementRef<HTMLImageElement>>('imageEl');

  private startX = 0;
  private startY = 0;
  private readonly ZOOM_STEP = 0.2;
  private readonly SWIPE_THRESHOLD = 56;

  /** Gestos táctiles */
  private touchMode: 'none' | 'pan' | 'swipe' | 'pinch' = 'none';
  private touchStartX = 0;
  private touchStartY = 0;
  private panOriginX = 0;
  private panOriginY = 0;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;

  transformStyle = computed(() => {
    return `rotate(${this.rotation()}deg) scale(${this.zoomLevel()}) translate(${this.translateX()}px, ${this.translateY()}px)`;
  });

  isPdfItem(item: DocViewerItem): boolean {
    return isPdfViewerItem(item);
  }

  next() { this.changeDoc(1); }
  prev() { this.changeDoc(-1); }

  private changeDoc(offset: number) {
    const nextIdx = this.activeIndex() + offset;
    if (this.isValidIndex(nextIdx)) {
      this.onSelect.emit(this.items()[nextIdx].id);
      this.reset();
    }
  }

  private isValidIndex(idx: number): boolean {
    return idx >= 0 && idx < this.items().length;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(e: KeyboardEvent) {
    if (e.key === 'ArrowRight') this.next();
    if (e.key === 'ArrowLeft') this.prev();
    if (e.key === 'Escape') this.close.emit();
  }

  @HostListener('window:mousemove', ['$event'])
  handleWindowMouseMove(e: MouseEvent) {
    if (this.isDragging() && !this.isPdf()) this.updatePos(e);
  }

  @HostListener('window:mouseup')
  handleWindowMouseUp() { this.stopDrag(); }

  onWheel(e: WheelEvent) {
    if (this.isPdf()) return;
    e.preventDefault();
    e.stopPropagation();
    this.updateZoom(e.deltaY < 0 ? this.ZOOM_STEP : -this.ZOOM_STEP);
  }

  zoomIn() { this.updateZoom(this.ZOOM_STEP); }
  zoomOut() { this.updateZoom(-this.ZOOM_STEP); }

  private updateZoom(delta: number) {
    if (this.isPdf()) return;
    this.setZoom(this.zoomLevel() + delta);
  }

  private setZoom(next: number) {
    if (this.isPdf()) return;
    const nZ = Math.min(Math.max(next, 0.2), 5);
    this.zoomLevel.set(nZ);
    if (nZ <= 1.01) this.resetPan();
  }

  private resetPan() {
    this.translateX.set(0);
    this.translateY.set(0);
  }

  startDrag(e: MouseEvent) {
    if (this.isPdf()) return;
    this.isDragging.set(true);
    this.startX = e.clientX - this.translateX();
    this.startY = e.clientY - this.translateY();
  }

  private updatePos(e: MouseEvent) {
    this.translateX.set(e.clientX - this.startX);
    this.translateY.set(e.clientY - this.startY);
  }

  stopDrag() { this.isDragging.set(false); }

  onTouchStart(e: TouchEvent): void {
    if (this.isPdf()) return;
    if (e.touches.length === 2) {
      this.touchMode = 'pinch';
      this.pinchStartDist = this.touchDistance(e.touches[0], e.touches[1]);
      this.pinchStartZoom = this.zoomLevel();
      this.isDragging.set(false);
      return;
    }
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    this.touchStartX = t.clientX;
    this.touchStartY = t.clientY;
    this.panOriginX = this.translateX();
    this.panOriginY = this.translateY();
    this.touchMode = this.zoomLevel() > 1.05 ? 'pan' : 'swipe';
    if (this.touchMode === 'pan') this.isDragging.set(true);
  }

  onTouchMove(e: TouchEvent): void {
    if (this.isPdf()) return;
    if (this.touchMode === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const dist = this.touchDistance(e.touches[0], e.touches[1]);
      if (this.pinchStartDist <= 0) return;
      this.setZoom(this.pinchStartZoom * (dist / this.pinchStartDist));
      return;
    }
    if (e.touches.length !== 1 || this.touchMode === 'none') return;
    const t = e.touches[0];
    const dx = t.clientX - this.touchStartX;
    const dy = t.clientY - this.touchStartY;

    if (this.touchMode === 'swipe') {
      // Si el gesto se vuelve claramente vertical o el usuario acerca, no forzar swipe.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 24) {
        this.touchMode = 'none';
      }
      return;
    }

    if (this.touchMode === 'pan') {
      e.preventDefault();
      this.translateX.set(this.panOriginX + dx);
      this.translateY.set(this.panOriginY + dy);
    }
  }

  onTouchEnd(e: TouchEvent): void {
    if (this.touchMode === 'pinch') {
      if (e.touches.length < 2) this.touchMode = 'none';
      this.isDragging.set(false);
      return;
    }
    if (this.touchMode === 'swipe' && e.changedTouches.length === 1) {
      const t = e.changedTouches[0];
      const dx = t.clientX - this.touchStartX;
      const dy = t.clientY - this.touchStartY;
      if (Math.abs(dx) >= this.SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) this.next();
        else this.prev();
      }
    }
    this.touchMode = 'none';
    this.isDragging.set(false);
  }

  private touchDistance(a: Touch, b: Touch): number {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  }

  reset() {
    this.zoomLevel.set(1);
    this.resetPan();
    this.rotation.set(0);
  }

  rotateImage(deg: number) {
    if (this.isPdf()) return;
    const img = this.imageElement()?.nativeElement;
    if (!img) return;
    const { canvas, ctx } = this.createCanvas(img);
    this.drawRotated(ctx, img, canvas, deg);
    this.onImageChange.emit(canvas.toDataURL('image/jpeg', 0.9));
    this.reset();
  }

  private createCanvas(img: HTMLImageElement) {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;
    return { canvas, ctx: canvas.getContext('2d')! };
  }

  private drawRotated(ctx: CanvasRenderingContext2D, img: HTMLImageElement, cvs: HTMLCanvasElement, deg: number) {
    ctx.clearRect(0, 0, cvs.width, cvs.height);
    ctx.translate(cvs.width / 2, cvs.height / 2);
    ctx.rotate((deg * Math.PI) / 180);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  }
}
